import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  alpha,
} from '@mui/material';
import { CheckCircle } from '@mui/icons-material';
import BaseTab from './BaseTab';

interface BackendDeploymentTabProps {
  isDark: boolean;
  colors: any;
  discordFont: any;
  glassStyle: any;
  lokiGradient: string;
  goldColor: string;
  isMobile: boolean;
}

const BackendDeploymentTab: React.FC<BackendDeploymentTabProps> = (props) => {
  return (
    <BaseTab
      {...props}
      title="Backend Deployment"
      emoji="🚀"
    >
      <Typography variant="h6" sx={{ color: props.goldColor, mb: 3 }}>
        Deployment Guide
      </Typography>

      <Paper sx={{ bgcolor: alpha('#051814', 0.8), p: 3, borderRadius: 2, mb: 3 }}>
        <Typography variant="h6" sx={{ color: props.goldColor, mb: 2 }}>
          Production Environment Variables
        </Typography>
        <Paper sx={{ bgcolor: '#1E1F22', p: 2, borderRadius: 1, mb: 2 }}>
          <Typography variant="body2" fontFamily="monospace" color={props.goldColor}>
            NODE_ENV=production<br />
            MONGODB_URI=your_production_db_uri<br />
            JWT_SECRET=secure_random_secret<br />
            FRONTEND_URL=https://your-domain.com<br />
            BACKEND_URL=https://api.your-domain.com
          </Typography>
        </Paper>
        <Typography variant="caption" color={props.colors.textSecondary}>
          Ensure all environment variables are properly configured for production
        </Typography>
      </Paper>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Card sx={{ bgcolor: alpha(props.goldColor, 0.05) }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2, color: props.goldColor }}>
                Docker Support
              </Typography>
              <Paper sx={{ bgcolor: '#1E1F22', p: 2, borderRadius: 1 }}>
                <Typography variant="body2" fontFamily="monospace" color={props.goldColor}>
                  FROM node:16-alpine<br />
                  WORKDIR /app<br />
                  COPY package*.json ./<br />
                  RUN npm ci --only=production<br />
                  COPY . .<br />
                  RUN npm run build<br />
                  EXPOSE 3000<br />
                  CMD ["npm", "start"]
                </Typography>
              </Paper>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <Card sx={{ bgcolor: alpha('#0F3B2E', 0.05) }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2, color: '#0F3B2E' }}>
                Deployment Checklist
              </Typography>
              <List dense>
                {[
                  'Set NODE_ENV=production',
                  'Configure production database',
                  'Enable HTTPS/WSS',
                  'Set up proper CORS',
                  'Configure logging',
                  'Set up monitoring',
                  'Enable backups',
                  'Configure CDN',
                ].map((item, idx) => (
                  <ListItem key={idx}>
                    <ListItemIcon>
                      <CheckCircle sx={{ color: props.goldColor }} />
                    </ListItemIcon>
                    <ListItemText 
                      primary={item} 
                      primaryTypographyProps={{ color: props.colors.textPrimary }}
                    />
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Typography variant="h6" sx={{ mb: 2, color: props.goldColor }}>
        Deployment Platforms
      </Typography>
      <Grid container spacing={2}>
        {[
          { platform: 'AWS', desc: 'EC2, ECS, Lambda', color: props.goldColor },
          { platform: 'Google Cloud', desc: 'App Engine, GKE', color: '#0F3B2E' },
          { platform: 'Azure', desc: 'App Service, AKS', color: '#1D5C4A' },
          { platform: 'Heroku', desc: 'Container Registry', color: props.goldColor },
          { platform: 'DigitalOcean', desc: 'App Platform', color: '#0F3B2E' },
          { platform: 'Railway', desc: 'Serverless', color: '#1D5C4A' },
        ].map((dep, idx) => (
          <Grid size={{ xs: 12, sm: 6, md: 4 }} key={idx}>
            <Paper sx={{ p: 2, textAlign: 'center', borderLeft: `4px solid ${dep.color}` }}>
              <Typography variant="subtitle2" fontWeight={700} sx={{ color: dep.color }}>
                {dep.platform}
              </Typography>
              <Typography variant="caption" color={props.colors.textSecondary}>
                {dep.desc}
              </Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>
    </BaseTab>
  );
};

export default BackendDeploymentTab;